import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { 
  Users, 
  Clock, 
  Download, 
  Search,
  Filter,
  Building,
  Home,
  BarChart3,
  TrendingUp,
  ChevronDown
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toUTCPlus5DateTime } from '../utils/time';

const AttendanceManagement = () => {
  const [attendanceLogs, setAttendanceLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const { user } = useAuth();

  const [filters, setFilters] = useState({
    date: '',
    mode: '',
    name: '',
    email: '',
    search: ''
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalRecords, setTotalRecords] = useState(0);
  const [showFilters, setShowFilters] = useState(false);

  const pageSizeOptions = [10, 25, 50, 100];

  const getDateRangeParams = (date) => {
    if (!date) return {};
    // Filter by calendar day in PKT (UTC+5), matching displayed times
    return {
      start_date: `${date}T00:00:00+05:00`,
      end_date: `${date}T23:59:59.999+05:00`,
    };
  };

  const fetchAttendanceLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', String(currentPage));
      params.append('page_size', String(pageSize));
      if (filters.mode) params.append('mode', filters.mode);

      const dateRange = getDateRangeParams(filters.date);
      if (dateRange.start_date) params.append('start_date', dateRange.start_date);
      if (dateRange.end_date) params.append('end_date', dateRange.end_date);

      const response = await axios.get(`/api/attendance/logs?${params}`);
      setAttendanceLogs(response.data.attendance_logs || []);
      setTotalRecords(response.data.total || 0);
    } catch (error) {
      toast.error('Failed to fetch attendance logs');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, filters.mode, filters.date]);

  const fetchStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const params = new URLSearchParams();
      const dateRange = getDateRangeParams(filters.date);
      if (dateRange.start_date) params.append('start_date', dateRange.start_date);
      if (dateRange.end_date) params.append('end_date', dateRange.end_date);

      const response = await axios.get(`/api/attendance/stats?${params}`);
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to fetch attendance statistics');
    } finally {
      setStatsLoading(false);
    }
  }, [filters.date]);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchAttendanceLogs();
    } else {
      setLoading(false);
    }
  }, [user, fetchAttendanceLogs]);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchStats();
    } else {
      setStatsLoading(false);
    }
  }, [user, fetchStats]);

  // Name/email/search still applied client-side on the current page
  const filteredLogs = attendanceLogs.filter(log => {
    if (filters.name) {
      const nameMatch = log.user_name && log.user_name.toLowerCase().includes(filters.name.toLowerCase());
      if (!nameMatch) return false;
    }

    if (filters.email) {
      const emailMatch = log.user_email && log.user_email.toLowerCase().includes(filters.email.toLowerCase());
      if (!emailMatch) return false;
    }

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const userMatch = (
        (log.user_name && log.user_name.toLowerCase().includes(searchLower)) ||
        (log.user_email && log.user_email.toLowerCase().includes(searchLower))
      );
      if (!userMatch) return false;
    }

    return true;
  });

  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    if (key === 'mode' || key === 'date') {
      setCurrentPage(1);
    }
  };

  const clearFilters = () => {
    setFilters({
      date: '',
      mode: '',
      name: '',
      email: '',
      search: ''
    });
    setCurrentPage(1);
  };

  const exportData = async () => {
    try {
      const dataToExport = filteredLogs;
      const headers = ['User Name', 'User Email', 'Login Time', 'Location', 'Mode', 'Office Location', 'Office Radius'];
      const csvContent = [
        headers.join(','),
        ...dataToExport.map(log => [
          log.user_name || '',
          log.user_email || '',
          log.login_time,
          log.location || '',
          log.mode,
          log.office_location || '',
          log.office_radius || ''
        ].map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `attendance_logs_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Exported ${dataToExport.length} records successfully`);
    } catch (error) {
      toast.error('Failed to export data');
    }
  };

  const getModeIcon = (mode) => {
    return mode === 'office' ? <Building className="h-4 w-4" /> : <Home className="h-4 w-4" />;
  };

  const getModeBadge = (mode) => {
    const colors = {
      office: 'bg-green-100 text-green-800',
      remote: 'bg-blue-100 text-blue-800'
    };
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${colors[mode]}`}>
        {getModeIcon(mode)}
        {mode.charAt(0).toUpperCase() + mode.slice(1)}
      </span>
    );
  };

  const formatDateTime = (dateTime) => {
    return toUTCPlus5DateTime(dateTime);
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxButtons = 5;
    let start = Math.max(1, currentPage - Math.floor(maxButtons / 2));
    let end = Math.min(totalPages, start + maxButtons - 1);
    start = Math.max(1, end - maxButtons + 1);
    for (let i = start; i <= end; i += 1) {
      pages.push(i);
    }
    return pages;
  };

  if (user?.role !== 'admin') {
    return (
      <div className="text-center py-12">
        <Users className="h-16 w-16 text-secondary-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-secondary-900 mb-2">Access Restricted</h3>
        <p className="text-secondary-600">You don't have permission to view attendance management</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="page-header">
        <h1 className="text-xl font-bold text-secondary-900">Attendance Management</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={exportData}
            className="btn-primary flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            Export CSV
          </button>
        </div>
      </div>

      {statsLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="card text-center animate-pulse">
              <div className="h-8 w-8 bg-secondary-200 rounded mx-auto mb-2" />
              <div className="h-7 w-16 bg-secondary-200 rounded mx-auto mb-2" />
              <div className="h-4 w-24 bg-secondary-100 rounded mx-auto" />
            </div>
          ))}
        </div>
      ) : stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="card text-center">
            <BarChart3 className="h-8 w-8 text-orange-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-secondary-900">{stats.total_logins}</div>
            <div className="text-sm text-secondary-600">Total Logins</div>
          </div>
          
          <div className="card text-center">
            <Building className="h-8 w-8 text-blue-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-secondary-900">{stats.office_logins}</div>
            <div className="text-sm text-secondary-600">Office Logins</div>
          </div>
          
          <div className="card text-center">
            <Home className="h-8 w-8 text-green-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-secondary-900">{stats.remote_logins}</div>
            <div className="text-sm text-secondary-600">Remote Logins</div>
          </div>
          
          <div className="card text-center">
            <TrendingUp className="h-8 w-8 text-purple-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-secondary-900">
              {stats.office_percentage > stats.remote_percentage ? 'Office' : 'Remote'}
            </div>
            <div className="text-sm text-secondary-600">Primary Mode</div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" />
            <input
              type="text"
              placeholder="Search by name or email on this page..."
              className="input-field pl-10"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.target.blur();
                }
              }}
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="btn-secondary flex items-center gap-2"
          >
            <Filter className="h-4 w-4" />
            Filters
            <ChevronDown className="h-4 w-4" />
          </button>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-secondary-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">Name</label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="Filter by name..."
                  value={filters.name}
                  onChange={(e) => handleFilterChange('name', e.target.value)}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">Email</label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="Filter by email..."
                  value={filters.email}
                  onChange={(e) => handleFilterChange('email', e.target.value)}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">Date</label>
                <input
                  type="date"
                  className="input-field"
                  value={filters.date}
                  onChange={(e) => handleFilterChange('date', e.target.value)}
                />
              </div>
            </div>
            
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">Mode</label>
                <select
                  className="input-field"
                  value={filters.mode}
                  onChange={(e) => handleFilterChange('mode', e.target.value)}
                >
                  <option value="">All Modes</option>
                  <option value="office">Office</option>
                  <option value="remote">Remote</option>
                </select>
              </div>
            </div>
            
            <div className="mt-4 flex items-center justify-between">
              <div className="text-sm text-secondary-600">
                Showing {filteredLogs.length} of {totalRecords} attendance records
                {(filters.date || filters.mode || filters.name || filters.email || filters.search) && (
                  <span className="ml-2 px-2 py-1 bg-primary-100 text-primary-700 rounded-full text-xs">
                    Filtered
                  </span>
                )}
              </div>
              <button
                onClick={clearFilters}
                className="btn-secondary text-sm"
              >
                Clear Filters
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="card">
        <h2 className="text-sm font-semibold mb-4">Attendance Logs</h2>
        
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="text-center py-8">
            <Clock className="h-16 w-16 text-secondary-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-secondary-900 mb-2">No Attendance Records</h3>
            <p className="text-secondary-600">
              {attendanceLogs.length === 0 
                ? 'No attendance logs found. Users will be tracked automatically when they log in.'
                : 'No records match your current filters. Try adjusting your search criteria.'
              }
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-secondary-200">
              <thead className="bg-secondary-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Time In
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Mode
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Location
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-secondary-200">
                {filteredLogs.map((log) => (
                  <tr key={log.id} className="hover:bg-secondary-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className={`h-8 w-8 rounded-full flex items-center justify-center mr-3 ${
                          log.mode === 'office' ? 'bg-green-100' : 'bg-blue-100'
                        }`}>
                          {getModeIcon(log.mode)}
                        </div>
                        <div className="text-sm font-medium text-secondary-900">
                          {log.user_name || 'Unknown User'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-secondary-600">
                        {log.user_email || 'No email'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-secondary-900">
                        {formatDateTime(log.login_time)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getModeBadge(log.mode)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-secondary-900">
                        {log.location || 'Unknown Location'}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-6">
          <div className="flex items-center gap-2">
            <span className="text-sm text-secondary-600">Show:</span>
            <select
              className="input-field w-20 text-sm"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              {pageSizeOptions.map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
            <span className="text-sm text-secondary-600">per page</span>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <button 
                className="btn-secondary px-3" 
                disabled={currentPage === 1} 
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Previous
              </button>
              {getPageNumbers().map((pageNum) => (
                <button
                  key={pageNum}
                  className={`px-3 py-1 rounded transition-colors ${
                    currentPage === pageNum 
                      ? 'bg-primary-600 text-white' 
                      : 'bg-secondary-100 text-secondary-700 hover:bg-secondary-200'
                  }`}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </button>
              ))}
              <button 
                className="btn-secondary px-3" 
                disabled={currentPage === totalPages} 
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </button>
            </div>
          )}
          
          <div className="text-sm text-secondary-600">
            Page {currentPage} of {totalPages}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendanceManagement;
